import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCar, FaBox } from 'react-icons/fa';
import './CreateMenu.css';

/**
 * CreateMenu - Dropdown menu component for the bottom navigation bar
 * Shows "Add Post" and "Send Package" options when the create button is clicked
 * Only visible on mobile devices (< 600px width)
 */
function CreateMenu({ isOpen, onClose }) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on the create button itself (to allow toggle behavior)
      // Check for both the button element and any of its children
      const clickedElement = event.target;
      const isCreateButton = clickedElement.closest('.nav-item.center-button');
      
      if (isCreateButton) {
        return; // Let the button handle the toggle
      }
      
      if (menuRef.current && !menuRef.current.contains(event.target) && isOpen) {
        onClose();
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle navigation and close menu
  const handleAddPost = () => {
    navigate('/PublishPost');
    onClose();
  };

  const handleSendPackage = () => {
    navigate('/SendPackage');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <div className="create-menu-backdrop" onClick={onClose} />
      
      {/* Menu container */}
      <div ref={menuRef} className={`create-menu ${isOpen ? 'open' : ''}`}>
        <div className="create-menu-options">
          <button 
            className="create-menu-option"
            onClick={handleAddPost}
          >
            <div className="option-icon">
              <FaCar />
            </div>
            <div className="option-content">
              <span className="option-title">{t('buttons.add_post')}</span>
              <span className="option-description">{t('create_menu.post_description') || 'Offer a ride or find passengers'}</span>
            </div>
          </button>

          <button 
            className="create-menu-option"
            onClick={handleSendPackage}
          >
            <div className="option-icon">
              <FaBox />
            </div>
            <div className="option-content">
              <span className="option-title">{t('buttons.package_post')}</span>
              <span className="option-description">{t('create_menu.package_description') || 'Send a package with a driver'}</span>
            </div>
          </button>
        </div>
      </div>
    </>
  );
}

export default CreateMenu;
